import request from '@utils/request'
import configRequest from '@src/config/request'

/**
 * 住宿啟用分店列表（Hotel Setting isEnable=1 且 state=1、hidden=0）
 * GET /api/v2/hotel/products
 */
export function fetchHotelProducts() {
  return request.get('v2/hotel/products', {
    ...configRequest,
    name: 'hotelProducts',
  })
}

/**
 * @param {{ merchant_id: string }} params 與子網域商家相同之 UUID
 */
export function fetchHotelCategories(params = {}) {
  return request.get('hotel/categories', {
    ...configRequest,
    name: 'hotelCategories',
    params,
  })
}

/**
 * 住宿分店目錄（價目表用，不需入住日）
 * GET /api/v2/hotel/products
 */
export function fetchHotelProductsV2(params = {}) {
  return request.get('v2/hotel/products', {
    ...configRequest,
    name: 'hotelProductsV2',
    params,
  })
}

/**
 * 住宿房型目錄（價目表用，不需入住日／不需 query）
 * GET /api/v2/hotel/room-types
 */
export function fetchHotelRoomTypesV2(params = {}) {
  return request.get('v2/hotel/room-types', {
    ...configRequest,
    name: 'hotelRoomTypesV2',
    params,
  })
}

/**
 * 單店房型預約查詢（日期庫存／適用性）
 * @param {{
 *   product_id: number|string,
 *   check_in?: string,
 *   check_out?: string,
 *   main_category_id?: number,
 *   room_type_id?: number,
 *   rooms?: string
 * }} params check_in／check_out 為後端語意：check_out＝最後住宿晚（Y-m-d）
 */
export function fetchHotelRoomTypeBooking(params) {
  return request.get('v2/hotel/room-type/booking', {
    ...configRequest,
    name: 'hotelRoomTypeBooking',
    params,
  })
}

/**
 * @deprecated 請改用 {@link fetchHotelRoomTypeBooking}
 */
export function fetchHotelRoomTypes(params) {
  return fetchHotelRoomTypeBooking(params)
}

/**
 * 取得住宿訂單容器（路徑：GET /api/hotel/order/container/{uuid}）
 * 回應含未付款結帳倒數：`reserveExpireDate`、`remainingSeconds`（與 `paymentExpiredDate`／`reserved_at`／暫存卡單時限對齊）。
 * @param {string} containerUuid
 * @param {object} [options] 覆寫 request 設定（如 errorHandle、showMessage）
 */
export function fetchHotelOrderContainer(containerUuid, options = {}) {
  const id = encodeURIComponent(String(containerUuid))
  return request.get(`hotel/order/container/${id}`, {
    ...configRequest,
    name: 'fetchHotelOrderContainer',
    ...options,
  })
}

/**
 * 建立暫存住宿訂單（保留秒數以後端 HOTEL_ORDER_RESERVE_TIME／API remaining_seconds 為準）
 * @param {{
 *   product_id: string,
 *   check_in: string,
 *   check_out: string,
 *   lines: { room_type_id: number, quantity: number }[],
 *   guests: { id: number, name: string }[] 可為 []，寵物於 Step1 指派／新增後補齊
 * }} payload check_out：最後住宿晚（Y-m-d），與搜尋頁「退房日」差一日時由呼叫端換算
 */
export function createTemporaryHotelOrder(payload) {
  return request.post('hotel/temporary-order', payload, {
    ...configRequest,
    name: 'hotelTemporaryOrder',
  })
}

/**
 * 取消臨時住宿訂單並釋放庫存（離開 Step1／重選房型）
 * 使用 POST cancel，避免部分環境對 DELETE 回 403。
 * @param {string} containerUuid 容器 UUID
 */
export function cancelTemporaryHotelOrder(containerUuid) {
  return request.post(
    'hotel/temporary-order/cancel',
    { container_uuid: String(containerUuid) },
    {
      ...configRequest,
      name: 'hotelTemporaryOrderCancel',
    }
  )
}

/**
 * Step1：寫入各房入住寵物；無待評估則重刷保留時間（與後台暫存單一致）。待評估時 pending_evaluation=true、is_temp 與 reserved_at 清空、remaining_seconds=0
 * @param {{
 *   container_uuid: string,
 *   rooms: { hotel_order_id?: number, guest_ids: number[] }[],
 * }} payload
 */
export function submitHotelTemporaryOrderStepOne(payload) {
  return request.post('hotel/temporary-order/step-one', payload, {
    ...configRequest,
    name: 'hotelTemporaryOrderStepOne',
  })
}

/**
 * 住宿試算：與後台 POST admin/hotel/{product_id}/order/calculate 相同參數（product_id、bookings、guests、addons、adminBackfill），不依賴訂單
 * @param {{
 *   product_id: string|number,
 *   bookings: { date: string, roomTypeId: number, roomId: number, guestCount?: number }[],
 *   guests: { id: number, name?: string }[],
 *   adminBackfill?: boolean,
 *   addons?: unknown,
 * }} payload
 */
export function calculateHotel(payload) {
  return request.post('hotel/calculate', payload, {
    ...configRequest,
    name: 'hotelCalculate',
  })
}
